import React, {useEffect, useState, useRef, useCallback} from "react";
import {Button, Form, Alert} from "react-bootstrap";
import * as t from "counterpart";
import {GoogleMap, LoadScript, Marker, Autocomplete} from "@react-google-maps/api";
import api from "../../../utilities/API";

// adding this directly shows error
const mapLibs = ["places"];

function Addresses(props)
{
    const [addresses, setAddresses] = useState([]);
    const [selected, setSelected] = useState({});
    const [errors, setErrors] = useState({validation: {}});
    const autoRef = useRef(null);
    const apiKey = window.portalSetting("web.google.maps.key");

    // load initial data
    useEffect(() => {
        api("commerce/addresses/", {
            result: ({data}) => {
                setAddresses(data || []);

                // set the default address or the first addres by default
                if(data.length) {
                    const a = data.find(i => i.default);
                    onChange(a || data[0]);
                }
            },
            error: alert
        });
    }, []);

    //
    const onChange = addr => {
        setSelected(addr);
        if(props.onChange) {
            props.onChange(addr.id);
        }
    }

    //
    const onAddressFormChange = ({target}) => setSelected({...selected, [target.name]: target.value});

    // add new address
    const onNewAddress = () =>
    {
        setErrors({validation: {}});

        api("commerce/addresses/", {
            method: "POST",
            data: selected,
            result: (data) => {
                setAddresses([...addresses, data]);
                onChange(data);
            },
            error: (error, details) => {
                if(details.response && details.response.status === 422)
                    error = details.response.data;

                setErrors({validation: {}, ...error});
            }
        });
    };

    //
    const onMapClick = useCallback((e) => {
        const location = {lat: e.latLng.lat(), lng: e.latLng.lng()};
        (new window.google.maps.Geocoder()).geocode(e.placeId ? {placeId: e.placeId} : { location}, (p) => {
            if(p) {
                const addr = parseAddress(p[0]);
                if(addr.country && addr.state && addr.city) {
                    setSelected({...selected, ...location, ...addr});
                }
            }
        });
    }, [selected]);

    //
    const autocompletePlace = () => {
        const p = autoRef.current?.getPlace();
        if(p?.geometry) {
            const l = p.geometry.location;
            const addr = parseAddress(p);
            if(addr.country && addr.state && addr.city) {
                setSelected({...selected, ...addr, lat: l.lat(), lng: l.lng()});
            }
        }
    }

    //
    const currentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                onMapClick({latLng: {lat: () => position.coords.latitude, lng: () => position.coords.longitude}})
            });
        }
    }

    return (
        <>
            <div className="col-md-6">
                <h3><span><i className="fa fa-location" /></span>{t("Shipping Address")}</h3>
                <div id="addresses">
                    {addresses.map(addr =>
                        <div
                            key={addr.id}
                            className={"address "+(addr.id === selected.id ? "selected" : "")}
                            onClick={() => onChange(addr)}>
                            <div>
                                <h6>{addr.name}</h6>
                                {addr.country} - {addr.state} - {addr.city} <br/>
                                {addr.street} {addr.building}, {addr.floor}, {addr.apartment}
                            </div>
                        </div>
                    )}

                    <div className={"address new "+(selected.new ? "selected" : "")} onClick={() => setSelected({new: true})}>
                        <div><i className="fa fa-plus" /> {t("Add New Address")}</div>
                    </div>
                </div>
            </div>

            <LoadScript googleMapsApiKey={apiKey} libraries={mapLibs}>
                {selected.new &&
                <div id="new-address" className="row">
                    <div className="col-md-6">
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '400px' }}
                            center={{ lat: selected.lat || 23.1010706, lng: selected.lng || 44.2407631 }} zoom={5.4}
                            onClick={onMapClick}
                        >
                            {selected.lat && selected.lng && <Marker position={{lat: selected.lat, lng: selected.lng}} />}

                            <Autocomplete onLoad={a => autoRef.current = a} onPlaceChanged={autocompletePlace}>
                                <input
                                    type="text"
                                    placeholder="Search for a place"
                                    style={{
                                        boxSizing: `border-box`,
                                        border: `1px solid transparent`,
                                        width: `240px`,
                                        height: `40px`,
                                        padding: `0 12px`,
                                        borderRadius: `3px`,
                                        boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
                                        fontSize: `14px`,
                                        outline: `none`,
                                        textOverflow: `ellipses`,
                                        position: "absolute",
                                        right: "64px",
                                        top: "10px",
                                        marginLeft: "-120px"
                                    }}
                                />
                            </Autocomplete>

                            <Button onClick={currentLocation} style={{position: "absolute", bottom: 10, left: 10}} variant="light">
                                <i className="fa fa-location-crosshairs" /> {t("Current Location")}
                            </Button>
                        </GoogleMap>
                    </div>

                    {(!selected.lat || !selected.lng) && <div className="col-md-6 d-flex align-items-center justify-content-center">
                        <h3>{t("Select your address on the map")}</h3>
                    </div>}

                    {selected.lat && selected.lng &&
                    <div className="col-md-6">
                        {errors.message && <Alert variant="danger">{t(errors.message)}</Alert>}

                        <Form.Group controlId="address_name">
                            <Form.Label>{t("Address Name")}</Form.Label>
                            <Form.Control name="name" value={selected.name || ""} onChange={onAddressFormChange} />
                            <div className="error">{t(errors?.validation?.name || " ")}</div>
                        </Form.Group>

                        <div className="row" style={{marginTop: 10}}>
                            <Form.Group controlId="country" className="col-md-4">
                                <Form.Label>{t("Country")}</Form.Label>
                                <Form.Control name="country" disabled value={selected.country ? new Intl.DisplayNames([t.getLocale()], {type: "region"}).of(selected.country) : ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.country || " ")}</div>
                            </Form.Group>

                            <Form.Group controlId="state" className="col-md-4">
                                <Form.Label>{t("State")}</Form.Label>
                                <Form.Control name="state" disabled value={selected.state || ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.state || " ")}</div>
                            </Form.Group>

                            <Form.Group controlId="city" className="col-md-4">
                                <Form.Label>{t("City")}</Form.Label>
                                <Form.Control name="city" disabled value={selected.city || ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.city || " ")}</div>
                            </Form.Group>
                        </div>

                        <Form.Group controlId="street">
                            <Form.Label>{t("Street")}</Form.Label>
                            <Form.Control name="street" value={selected.street || ""} onChange={onAddressFormChange} />
                            <div className="error">{t(errors?.validation?.street || " ")}</div>
                        </Form.Group>

                        <div className="row" style={{marginTop: 10}}>
                            <Form.Group controlId="building" className="col-md-4">
                                <Form.Label>{t("Building")}</Form.Label>
                                <Form.Control name="building" value={selected.building || ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.building || " ")}</div>
                            </Form.Group>

                            <Form.Group controlId="floor" className="col-md-4">
                                <Form.Label>{t("Floor")}</Form.Label>
                                <Form.Control name="floor" value={selected.floor || ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.floor || " ")}</div>
                            </Form.Group>

                            <Form.Group controlId="apartment" className="col-md-4">
                                <Form.Label>{t("Apartment")}</Form.Label>
                                <Form.Control name="apartment" value={selected.apartment || ""} onChange={onAddressFormChange} />
                                <div className="error">{t(errors?.validation?.apartment || " ")}</div>
                            </Form.Group>
                        </div>

                        <Form.Group controlId="default" style={{marginTop: 10}}>
                            <Form.Check
                                name="default" inline type="switch"
                                label={t("Set as default address")}
                                value={selected.default || ""}
                                onChange={onAddressFormChange}
                            />
                        </Form.Group>

                        <Button variant="danger" onClick={onNewAddress} style={{marginTop: 10}}>{t("Save Address")}</Button>
                    </div>}
                </div>}
            </LoadScript>
        </>
    )
}

//
function parseAddress(p={})
{
    let addr = {
        name: p.formatted_address,
        country: null,
        state: null,
        city: null,
        street: null,
        building: null,
        floor: null,
        apartment: null,
        "default": false
    };

    p.address_components?.forEach(a => {
        a.types.forEach(t => {
            if(t === "country" && a.types.includes("political")) {
                addr.country = a.short_name;
            } else if(t === "administrative_area_level_1" && a.types.includes("political")) {
                addr.state = a.short_name;
            } else if((t === "administrative_area_level_2" || t === "locality") && a.types.includes("political")) {
                addr.city = a.short_name;
            } else if(t === "route") {
                addr.street = a.long_name;
            }
        })
    });

    return addr;
}

export default Addresses;
